import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './RentStatus.css';
import RentStatusDetails from './RentStatusDetails';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import { getMyProperties, getPropertyById, getPropertyUnits } from '../../../api/services/propertyService';
import { computePropertyUnitStats, parseUnitsFromApiResponse } from '../../../utils/propertyUnitStats';
import { hydrateUnitsWithActiveTenancy } from '../../../utils/propertyUnitTenancy';
import { getMyAgentAssignments } from '../../../api/services/agentAssignmentsService';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import { storeImageInCache, getImageFromCache, BROKEN_IMAGE_PLACEHOLDER_SRC } from '../../../utils/imageCache';

const RentStatus = ({ isAgentView = false }) => {
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [properties, setProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authenticatedImages, setAuthenticatedImages] = useState({}); // { propertyId_imageUrl: blobUrl }

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (isAgentView) {
        const response = await getMyAgentAssignments({ page: 0, size: 50 });
        const assignments = response?.data?.content || [];
        
        // Fetch full property details for each assignment to get images and accurate stats
        const enrichedProperties = await Promise.all(
          assignments.map(async (a) => {
            try {
              const detailsRes = await getPropertyById(a.propertyId, true);
              const p = detailsRes?.data || {};
              let units = Array.isArray(p.units) ? p.units : [];
              try {
                const unitsRes = await getPropertyUnits(a.propertyId, { dashboardScope: 'RENTAL' });
                units = parseUnitsFromApiResponse(unitsRes);
                units = await hydrateUnitsWithActiveTenancy(a.propertyId, units);
              } catch (_) {
                // Keep units from property details if units endpoint fails
              }
              const property = { ...p, id: a.propertyId, units };
              const stats = computePropertyUnitStats(property, { propertyScope: 'landlord' });
              return {
                id: a.propertyId,
                name: p.name || a.propertyName,
                address: p.address || a.propertyAddress,
                unitCount: stats.totalUnits,
                occupiedCount: stats.occupiedCount,
                vacantCount: stats.vacantCount,
                imageUrls: p.imageUrls || [],
                imageUrl: p.imageUrl || '',
              };
            } catch (err) {
              const totalUnits = a.totalUnitsCount || 0;
              const occupiedCount = a.occupiedUnitsCount || 0;
              return {
                id: a.propertyId,
                name: a.propertyName,
                address: a.propertyAddress,
                unitCount: totalUnits,
                occupiedCount,
                vacantCount: Math.max(0, totalUnits - occupiedCount),
              };
            }
          })
        );
        setProperties(enrichedProperties);
      } else {
        const response = await getMyProperties({ page: 0, size: 50 });
        const list = response?.data?.content || [];
        
        // Fetch full property details for each property to ensure images and accurate stats
        const enrichedProperties = await Promise.all(
          list.map(async (property) => {
            const propertyId = property?.id;
            if (!propertyId) return property;
            try {
              const detailsRes = await getPropertyById(propertyId, true);
              const p = detailsRes?.data || {};
              let units = Array.isArray(p.units) ? p.units : [];
              try {
                const unitsRes = await getPropertyUnits(propertyId, { dashboardScope: 'RENTAL' });
                units = parseUnitsFromApiResponse(unitsRes);
                units = await hydrateUnitsWithActiveTenancy(propertyId, units);
              } catch (_) {
                // Keep units from property details if units endpoint fails
              }
              const merged = { ...property, ...p, units };
              const stats = computePropertyUnitStats(merged, { propertyScope: 'landlord' });
              return {
                ...merged,
                unitCount: stats.totalUnits,
                occupiedCount: stats.occupiedCount,
                vacantCount: stats.vacantCount,
                totalRent: units.reduce((sum, u) => sum + (u.monthlyRent || u.rent || 0), 0),
              };
            } catch (err) {
              return property;
            }
          })
        );
        setProperties(enrichedProperties);
      }
    } catch (err) {
      setError(err.message || 'Failed to load properties');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isAgentView]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // Sync state with browser history for back-button support
  useEffect(() => {
    if (selectedPropertyId) {
      window.history.pushState({ view: 'rent-status-details', id: selectedPropertyId }, '');
    }

    const handlePopState = (event) => {
      // If we go back, and the detail view was open, close it
      if (selectedPropertyId) {
        setSelectedPropertyId(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedPropertyId]);

  const filteredProperties = useMemo(() => {
    if (!searchQuery.trim()) return properties;
    const query = searchQuery.toLowerCase().trim();
    return properties.filter(property => {
      return (
        property.name?.toLowerCase().includes(query) ||
        property.address?.toLowerCase().includes(query)
      );
    });
  }, [properties, searchQuery]);

  // Fetch authenticated images for all properties
  useEffect(() => {
    const loadAuthenticatedImages = async () => {
      const newAuthenticatedImages = {};

      for (const property of filteredProperties) {
        const imageUrl = property.imageUrls?.[0] || property.imageUrl || null;
        if (!imageUrl) continue;

        const imageKey = `${property.id}_${imageUrl}`;

        // Skip if already a blob URL or data URL
        if (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
          newAuthenticatedImages[imageKey] = imageUrl;
          continue;
        }

        // Skip if no URL
        if (!imageUrl.startsWith('http')) {
          continue;
        }

        try {
          // Check cache first
          const cachedImage = getImageFromCache(imageUrl);
          if (cachedImage) {
            newAuthenticatedImages[imageKey] = cachedImage;
          } else {
            // Fetch from server
            const blobUrl = await fetchAuthenticatedImage(imageUrl);
            if (blobUrl) {
              newAuthenticatedImages[imageKey] = blobUrl;
              await storeImageInCache(imageUrl, blobUrl);
            }
          }
        } catch (error) {
          console.error(`Error loading image for property ${property.id}:`, error);
        }
      }

      setAuthenticatedImages(newAuthenticatedImages);
    };

    if (filteredProperties.length > 0) {
      loadAuthenticatedImages();
    }
  }, [filteredProperties]);

  // If a property is selected, show RentStatusDetails
  if (selectedPropertyId) {
    return (
      <RentStatusDetails 
        propertyId={selectedPropertyId} 
        onBack={() => setSelectedPropertyId(null)}
      />
    );
  }

  if (loading && properties.length === 0) {
    return (
      <div className="rent-status-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>Loading properties...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="rent-status-container">
      <CollapsibleTopPanel id="rent-status-top-panel" defaultOpenOnMobile={false}>
        <div className="page-header-premium">
          <div className="header-left">
            <h1 className="page-title-main">Rent Status Overview</h1>
          </div>
          <div className="header-actions-premium">
            <div className="search-box-pill">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </CollapsibleTopPanel>

      {error && (
        <div className="error-card">
          <p>{error}</p>
          <button className="btn-primary-premium" style={{ width: 'auto', padding: '0 20px' }} onClick={fetchProperties}>Retry</button>
        </div>
      )}

      {loading && properties.length === 0 ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your properties...</p>
        </div>
      ) : properties.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-icon">🏠</div>
          <h3>No properties found</h3>
          <p>{isAgentView ? 'You don\'t have any assigned properties yet.' : 'Please add properties in Manage Rentals first.'}</p>
        </div>
      ) : (
        <div className="properties-grid-overview">
          {filteredProperties.map((property) => {
            const stats = computePropertyUnitStats(property, { propertyScope: 'landlord' });
            const { totalUnits, occupiedCount, vacantCount } = stats;
            
            // Get first image or fallback
            const rawImageUrl = property.imageUrls?.[0] || property.imageUrl || null;
            const displayImage = authenticatedImages[`${property.id}_${rawImageUrl}`] || rawImageUrl;

            return (
              <div key={property.id} className="property-overview-card">
                <div className="property-images-container">
                  <div className="property-images-grid single-image">
                    <div className="property-image-wrapper main-image">
                      {displayImage ? (
                        <img 
                          src={displayImage} 
                          alt={property.name} 
                          className="property-image" 
                          onError={(e) => {
                            e.currentTarget.src = BROKEN_IMAGE_PLACEHOLDER_SRC;
                          }}
                        />
                      ) : (
                        <div className="property-avatar-placeholder">
                          {property.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="property-overview-header">
                  <h3>{property.name}</h3>
                  <p className="property-overview-address">{property.address}</p>
                </div>

                <div className="property-overview-stats">
                  <div className="property-stat-item">
                    <span className="stat-label">TOTAL</span>
                    <span className="stat-value">{totalUnits}</span>
                  </div>
                  <div className="property-stat-item success">
                    <span className="stat-label">OCCUPIED</span>
                    <span className="stat-value">{occupiedCount}</span>
                  </div>
                  <div className="property-stat-item warning">
                    <span className="stat-label">VACANT</span>
                    <span className="stat-value">{vacantCount}</span>
                  </div>
                </div>

                <div className="property-action-buttons">
                  <button 
                    className="btn-view-financials"
                    onClick={() => setSelectedPropertyId(property.id)}
                  >
                    View Financials
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RentStatus;
